import React, { Fragment, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Table from './Table';
import FormModal from './FormModal';
import DeleteModal from './DeleteModal';
import UploadModal from './UploadModal';
import './AdminForms.scss';
import { Helmet } from 'react-helmet';
import Layout from './../Layout';
import * as actions from './AdminForms.actions';
import * as selectors from './AdminForms.selectors';
import { stringifyEqualityCheck } from './../../utils';
import * as dialog from './../common/dialog';
import * as loginSelectors from './../Login/Login.selectors';
import { showNotification } from './../../utils';

const AdminForms = (props) => {
    /* State to props */
    const forms = useSelector(selectors.getForms, stringifyEqualityCheck);
    const fetching = useSelector(selectors.getFetching);
    const saving = useSelector(selectors.getSaving);
    const isDeleting = useSelector(selectors.getIsDeleting);
    const idModal = useSelector(selectors.getIdModal);
    const pagination = useSelector(selectors.getPagination, stringifyEqualityCheck);
    const pdfFile = useSelector(selectors.getPdfFile, stringifyEqualityCheck);
    const previewFile = useSelector(selectors.getPreviewFile, stringifyEqualityCheck);
    const form = useSelector(selectors.getForm, stringifyEqualityCheck);
    const departments = useSelector(selectors.getDepartments, stringifyEqualityCheck);
    const sort = useSelector(selectors.getSort, stringifyEqualityCheck);
    const search = useSelector(selectors.getSearch);
    const searchType = useSelector(selectors.getSearchType);
    const isUploading = useSelector(selectors.getIsUploading);
    const uploadFile = useSelector(selectors.getUploadFile, stringifyEqualityCheck);
    const allDepartments = useSelector(selectors.getAllDepartments, stringifyEqualityCheck);
    const user = useSelector(loginSelectors.getUser, stringifyEqualityCheck);

    /* Dispatch to props */
    const dispatch = useDispatch();
    const fetchForms = useCallback((type) => dispatch(actions.fetchForms(type)), [dispatch]);
    const setIdModal = useCallback((id) => dispatch(actions.setIdModal(id)), [dispatch]);
    const setActivePage = useCallback((value) => dispatch(actions.setActivePage(value)), [dispatch]);
    const resetState = useCallback(() => dispatch(actions.resetState()), [dispatch]);
    const setForm = useCallback((id) => dispatch(actions.setForm(id)), [dispatch]);
    const setFormField = useCallback((field, value) => dispatch(actions.setFormField(field, value)), [dispatch]);
    const setPdfFile = useCallback((file) => dispatch(actions.setPdfFile(file)), [dispatch]);
    const setPreviewFile = useCallback((file) => dispatch(actions.setPreviewFile(file)), [dispatch]);
    const resetForm = useCallback(() => dispatch(actions.resetForm()), [dispatch]);
    const fetchDepartments = useCallback(() => dispatch(actions.fetchDepartments()), [dispatch]);
    const saveForm = useCallback(() => dispatch(actions.saveForm()), [dispatch]);
    const deleteForm = useCallback(() => dispatch(actions.deleteForm()), [dispatch]);
    const setSort = useCallback((column, type) => dispatch(actions.setSort(column, type)), [dispatch]);
    const setSearch = useCallback((value) => dispatch(actions.setSearch(value)), [dispatch]);
    const setSearchType = useCallback((value) => dispatch(actions.setSearchType(value)), [dispatch]);
    const setUploadFile = useCallback((file) => dispatch(actions.setUploadFile(file)), [dispatch]);
    const uploadExcel = useCallback(() => dispatch(actions.uploadExcel()), [dispatch]);
    const addDepartment = useCallback((id) => dispatch(actions.addDepartment(id)), [dispatch]);
    const deleteDepartment = useCallback((index) => dispatch(actions.deleteDepartment(index)), [dispatch]);
    const showFormModal = useCallback(() => dispatch(dialog.actions.showDialog('form-modal')), [dispatch]);
    const hideFormModal = useCallback(() => dispatch(dialog.actions.hideDialog('form-modal')), [dispatch]);
    const showDeleteModal = useCallback(() => dispatch(dialog.actions.showDialog('delete-modal')), [dispatch]);
    const hideDeleteModal = useCallback(() => dispatch(dialog.actions.hideDialog('delete-modal')), [dispatch]);
    const showUploadModal = useCallback(() => dispatch(dialog.actions.showDialog('upload-modal')), [dispatch]);
    const hideUploadModal = useCallback(() => dispatch(dialog.actions.hideDialog('upload-modal')), [dispatch]);

    /* componentDidMount */
    useEffect(() => {
        fetchForms();
        fetchDepartments();
        return () => {
            hideFormModal();
            resetState();
        };
    }, [fetchForms, resetState, fetchDepartments, hideFormModal]);

    let permissions = [];
    if (user && user.uth_permissions != null) {
        permissions = JSON.parse(user.uth_permissions);
    }

    const handleSearch = () => {
        if (props.search === '') {
            return showNotification('Enter a value', 'Enter a text to search', 'info');
        }

        setActivePage(1);
        fetchForms();
    };

    const keyPressHandler = (event) => {
        if (event.key === 'Enter') {
            if (props.search === '') {
                return showNotification('Enter a value', 'Enter a text to search', 'info');
            }

            setActivePage(1);
            fetchForms();
        }
    };

    const exportHandler = () => {
        window.open(process.env.REACT_APP_API_URL + '/uthealth-static-template/export-excel');
    };

    const uploadHandler = () => {
        showUploadModal();
    }

    let createButton = null;
    if (permissions.includes('forms-create')) {
        createButton = <div className="create-button" onClick={showFormModal}><i className="fas fa-folder-plus" /> Add Form</div>;
    }

    let exportButton = null;
    if (permissions.includes('forms-export')) {
        exportButton = <div className="export-button" onClick={exportHandler}><i className="fas fa-cloud-download-alt" /> Export Excel</div>;
    }

    let uploadButton = null;
    if (permissions.includes('forms-import')) {
        uploadButton = <div className="upload-button" onClick={uploadHandler}><i className="fas fa-cloud-upload-alt" /> Import Excel</div>;
    }

    return (
        <Layout
            title={<Fragment><i className="fas fa-file-alt"></i> Forms</Fragment>}
            history={props.history}
        >
            <Helmet><title>Forms</title></Helmet>
            <div className="admin-forms-container">
                <h2>Forms</h2>
                {createButton}
                {exportButton}
                {uploadButton}
                <div className="search-container">
                    <div class="search-field">
                        <input
                            type="text"
                            placeholder="Search here..."
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            onKeyPress={keyPressHandler}
                        />
                        <select
                            value={searchType}
                            onChange={(event) => setSearchType(event.target.value)}
                        >
                            <option value="id">By ID</option>
                            <option value="sku">By SKU</option>
                            <option value="name">By Name</option>
                            <option value="department">By Department</option>
                            <option value="pieces_per">By Pieces Per</option>
                        </select>
                        <div className="button" onClick={handleSearch}>Search</div>
                    </div>
                </div>
                <Table
                    forms={forms}
                    fetching={fetching}
                    user={user}
                    pagination={pagination}
                    sort={sort}
                    showFormModal={showFormModal}
                    setIdModal={setIdModal}
                    setActivePage={setActivePage}
                    setForm={setForm}
                    showDeleteModal={showDeleteModal}
                    setSort={setSort}
                />
            </div>
            <FormModal
                form={form}
                saving={saving}
                idModal={idModal}
                pdfFile={pdfFile}
                previewFile={previewFile}
                departments={departments}
                allDepartments={allDepartments}
                hideFormModal={hideFormModal}
                setIdModal={setIdModal}
                setFormField={setFormField}
                setForm={setForm}
                setPdfFile={setPdfFile}
                setPreviewFile={setPreviewFile}
                resetForm={resetForm}
                saveForm={saveForm}
                addDepartment={addDepartment}
                deleteDepartment={deleteDepartment}
            />
            <DeleteModal
                idModal={idModal}
                isDeleting={isDeleting}
                deleteForm={deleteForm}
                hideDeleteModal={hideDeleteModal}
                setIdModal={setIdModal}
            />
            <UploadModal
                isUploading={isUploading}
                uploadFile={uploadFile}
                setUploadFile={setUploadFile}
                uploadExcel={uploadExcel}
                hideUploadModal={hideUploadModal}
            />
        </Layout>
    );
};

export default AdminForms;
